import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectorRef } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { _AbcSelectBoxComponent } from '@barthauer/abc/ui-components/abc-select-box';
import { _AbcButtonComponent } from '@barthauer/abc/ui-components/abc-button';
import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import { DataUrl, MimeTypeEnum } from '@barthauer/abc/core';

export class CameraDevice {
  label: string;
  deviceId: string;
  displayText: string;
}

@Component({
  selector: 'lib-camera-preview',
  standalone: true,
  imports: [
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatSelectModule,
    MatFormFieldModule,
    _AbcSelectBoxComponent,
    _AbcButtonComponent,
  ],
  templateUrl: './camera-preview.component.html',
  styleUrl: './camera-preview.component.scss',
})
export class CameraPreviewComponent implements OnInit, OnDestroy {
  @Output() onCapture = new EventEmitter<DataUrl>();
  private videoTag: HTMLVideoElement;
  private stream: MediaStream | null = null;
  public selectedCameraDevice: CameraDevice;
  public cameraDevices: CameraDevice[] = [];
  public showCameraSelection: boolean = false;
  public cameraError: string = '';
  protected res: ResourceSet;
  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private resourceManagerService: ResourceManagerService,
  ) {}

  async ngOnInit() {
    this.res = await this.resourceManagerService.getResourceSet('abc-media-gallery');
    this.initializeCamera();
  }

  ngOnDestroy() {
    this.stopVideo();
  }

  private async initializeCamera(): Promise<void> {
    this.videoTag = document.getElementById('camera-video') as HTMLVideoElement;

    if (!this.videoTag) {
      console.error('Video element not found');
      return;
    }

    try {
      // Request camera access
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: true,
      });

      // Enumerate camera devices
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter((device) => device.kind === 'videoinput');

      this.showCameraSelection = videoDevices.length > 1;

      // Build camera devices list to handle duplicate names
      const labelCounts = new Map<string, number>();
      videoDevices.forEach((device) => {
        const label = device.label || `Camera`;
        labelCounts.set(label, (labelCounts.get(label) || 0) + 1);
      });

      const labelIndices = new Map<string, number>();
      videoDevices.forEach((device) => {
        const label = device.label || `Camera`;
        let displayText = label;

        if ((labelCounts.get(label) || 0) > 1) {
          const currentIndex = labelIndices.get(label) || 0;
          displayText = `${label}[${currentIndex}]`;
          labelIndices.set(label, currentIndex + 1);
        }

        const newCameraDevice: CameraDevice = {
          label: label,
          deviceId: device.deviceId,
          displayText: displayText,
        };
        this.cameraDevices.push(newCameraDevice);
      });

      // Select the last camera (usually back camera on mobile)
      if (videoDevices.length > 1) {
        this.selectedCameraDevice = this.cameraDevices[this.cameraDevices.length - 1];
      } else {
        this.selectedCameraDevice = this.cameraDevices[0];
      }

      this.startVideo();
    } catch (error) {
      console.error('Error accessing camera:', error);
      this.cameraError = this.res?.get('CameraPreview_Error_Permissions');
      setTimeout(() => {
        this.changeDetectorRef?.detectChanges();
      });
    }
  }

  public startVideo(): void {
    if (!this.selectedCameraDevice) {
      return;
    }

    navigator.mediaDevices
      .getUserMedia({
        audio: false,
        video: {
          deviceId: { exact: this.selectedCameraDevice.deviceId },
        },
      })
      .then((stream) => {
        this.stream = stream;
        this.videoTag.srcObject = stream;
        this.videoTag.play();
        this.cameraError = '';
        setTimeout(() => {
          this.changeDetectorRef?.detectChanges();
        });
      })
      .catch((error) => {
        console.error('Error starting video:', error);
        this.cameraError = this.res?.get('CameraPreview_Error_Common');
        setTimeout(() => {
          this.changeDetectorRef?.detectChanges();
        });
      });
  }

  private stopVideo(): void {
    if (this.stream) {
      this.stream.getTracks().forEach((track) => {
        track.stop();
      });

      // Video-Element entkoppeln
      if (this.videoTag) {
        this.videoTag.pause();
        this.videoTag.srcObject = null;
        this.videoTag.load(); // wichtig bei Safari / iOS
      }

      this.stream = undefined;
    }
  }

  public onCameraDeviceChange(): void {
    this.stopVideo();
    this.startVideo();
  }

  public capture(): void {
    if (!this.videoTag) {
      return;
    }

    const canvas = document.getElementById('camera-canvas') as HTMLCanvasElement;
    if (!canvas) {
      return;
    }

    // Set canvas size to video dimensions
    canvas.width = this.videoTag.videoWidth;
    canvas.height = this.videoTag.videoHeight;

    // Draw video frame to canvas
    const context = canvas.getContext('2d');
    context?.drawImage(this.videoTag, 0, 0);

    // Get image data URL
    const dataUrl = DataUrl.fromString(canvas.toDataURL(MimeTypeEnum.JPEG));
    this.stopVideo();

    // Emit captured image
    this.onCapture.emit(dataUrl);
  }
}
